/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LoggingService, Logger } from '../logging/logging.service';
import { DbCacheConfigService } from './db-cache-config.service';
import { DbService } from './db.service';
import { DbCache } from './models/db-cache.class';
import { DbItemMetadeta, DbCacheMap, DbCacheDefinitionMap } from './models/db.models';

/**
 * @name dbCacheService
 * @description
 * dbCacheService is an optional layer on top of dbService. It provides a place to
 * store database information in memory so that it can be accessed quickly and
 * synchronously.
 * As an example, we can use it to control the state of a switch element that is
 * stored in the DB. When the page loads, without a memory cache you'd need to show
 * a spinner on each page load, or see the switch toggle on its own after the database
 * promise resolves. Instead we can use the cache service to keep it in memory for us
 *
 * @example
 * // During config time, tell cache provider which db keys to cache
 * dbCacheServiceProvider.loadGlobalKey('globalDatastoreName', 'key');
 * dbCacheServiceProvider.loadUserKey('userDatastoreName', 'key');
 *
 * // On app load, we can tell the cache service to load the global data
 * // into the cache
 *
 * // A controller can inject the service and access the cached data
 * var cachedData = dbCacheService.getCachedGlobalItem('storeName', 'key');
 * var cachedData = dbCacheService.getCachedUserItem(userId, storeName, key);
 * console.log(cachedData)
 *  -> {
 *  ->   item1: 'some data from the db',
 *  ->   status_: <One of loading, dirty, saved, or notFound>
 *  -> }
 * // The status will be loading until the data is initially cached from the db
 * // It will be saved when the cache and the db match
 * // It will briefly be 'dirty' when you update the cache and it queues a change
 * // to the database
 *
 * // This loads the data from the database into the cache, returning an observable
 * dbCacheService.syncGlobal('storeName');
 * dbCacheService.syncUser('storeName', userId);
 */

@Injectable({
    providedIn: 'root'
})
export class DbCacheService {
    private readonly logger: Logger;
    private readonly globalCacheDefinitions: DbCacheDefinitionMap;
    private readonly userCacheDefinitions: DbCacheDefinitionMap;
    private readonly globalCache: DbCacheMap;
    private readonly userCacheMap: Map<string, DbCacheMap>;

    constructor(
        private loggingService: LoggingService,
        dbCacheConfigService: DbCacheConfigService,
        private dbService: DbService
    ) {
        this.logger = this.loggingService.getLogger('crimson/dbCacheService');
        this.globalCacheDefinitions = dbCacheConfigService.globalCacheDefinitions;
        this.userCacheDefinitions = dbCacheConfigService.userCacheDefinitions;
        this.globalCache = dbCacheConfigService.globalCache;
        this.userCacheMap = dbCacheConfigService.userCache;

        this.dbService.dbHandler.onDbItemChange.subscribe((dbItemMetadata: DbItemMetadeta) => {
            let cacheItem: DbCache;
            if (dbItemMetadata.userId) {
                if (
                    this.userCacheMap.has(dbItemMetadata.userId) &&
                    this.userCacheMap.get(dbItemMetadata.userId).has(dbItemMetadata.storeName)
                ) {
                    const storeMap = this.userCacheMap.get(dbItemMetadata.userId).get(dbItemMetadata.storeName);
                    cacheItem = storeMap.get(dbItemMetadata.key);
                }
            } else {
                if (this.globalCache.has(dbItemMetadata.storeName)) {
                    cacheItem = this.globalCache.get(dbItemMetadata.storeName).get(dbItemMetadata.key);
                }
            }

            if (cacheItem) {
                // The DB changed and we are synchronizing this block-key
                cacheItem.sync_().subscribe();
            }
        });
    }

    syncGlobal(storeName: string): Observable<any> {
        const definitions = this.globalCacheDefinitions.get(storeName);
        const parallelSyncs: Observable<any>[] = [];
        definitions.forEach(key => {
            const storeMap = this.globalCache.get(storeName);
            parallelSyncs.push(storeMap.get(key).sync_());
        });
        return forkJoin(parallelSyncs).pipe(
            tap(() => {
                this.logger.info(`Cached DB store ${storeName} into memory`);
            })
        );
    }

    syncUser(storeName: string, userId: string): Observable<any> {
        const definitions = this.userCacheDefinitions.get(storeName);
        if (!this.userCacheMap.has(userId)) {
            this.initUserCache(userId);
        }
        const parallelSyncs: Observable<any>[] = [];
        definitions.forEach(key => {
            const storeMap = this.userCacheMap.get(userId).get(storeName);
            parallelSyncs.push(storeMap.get(key).sync_());
        });
        return forkJoin(parallelSyncs).pipe(
            tap(() => {
                this.logger.info(`Cached user DB store ${storeName} into memory for user ${userId}`);
            })
        );
    }

    private initUserCache(userId: string) {
        this.userCacheMap.set(userId, new Map());
        this.userCacheDefinitions.forEach((keySet, storeName) => {
            keySet.forEach(key => {
                if (!this.userCacheMap.get(userId).has(storeName)) {
                    this.userCacheMap.get(userId).set(storeName, new Map());
                }
                const storeMap = this.userCacheMap.get(userId).get(storeName);
                storeMap.set(key, new DbCache(this.dbService.dbHandler, storeName, key, userId));
            });
        });
    }

    getCachedGlobalItem(storeName: string, key: string): DbCache | undefined {
        if (!this.globalCache.has(storeName)) {
            return undefined;
        }
        return this.globalCache.get(storeName).get(key);
    }

    getOrCreateCachedGlobalItem(storeName: string, key: string) {
        if (!this.globalCache.has(storeName) || !this.globalCache.get(storeName).has(key)) {
            if (!this.globalCacheDefinitions.has(storeName)) {
                this.globalCacheDefinitions.set(storeName, new Set());
            }
            this.globalCacheDefinitions.get(storeName).add(key);
            if (!this.globalCache.has(storeName)) {
                this.globalCache.set(storeName, new Map());
            }
            this.globalCache.get(storeName).set(key, new DbCache(this.dbService.dbHandler, storeName, key));
        }
        return this.globalCache.get(storeName).get(key);
    }

    getCachedUserItem(userId: string, storeName: string, key: string) {
        if (!this.userCacheMap.has(userId)) {
            this.initUserCache(userId);
        }
        const storeMap = this.userCacheMap.get(userId).get(storeName);
        return storeMap.get(key);
    }
}
